import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  formatInfluencerNumber,
  sumInfluencerPerformances,
  type InfluencerMonthlyReport,
  type InfluencerPerformance,
} from '@/utils/approval/influencerMonthlyReport';

type ContentProps = {
  /** null 이면 조회 전·실패 — 표 머리글만 */
  report: InfluencerMonthlyReport | null;
  t: TFunction;
};

// 제목·표 셀은 React 가 다시 escape — i18next 기본 escape 를 두면 '&' 가 '&amp;' 로 보이고 저장됨
const NO_ESCAPE = { interpolation: { escapeValue: false } };

const listOf = <T,>(value: T[] | undefined): T[] =>
  Array.isArray(value) ? value : [];

export const getInfluencerMonthlySubject = (brandName: string, t: TFunction) =>
  t('{{brand}} 인플루언서 증정의 건', { brand: brandName, ...NO_ESCAPE });

const ReportInfoTable = ({
  report,
  t,
}: {
  report: InfluencerMonthlyReport;
  t: TFunction;
}) => {
  const brand = report.brandName ?? '';
  const rows: [string, string][] = [
    ['진행업무', getInfluencerMonthlySubject(brand, t)],
    ['진행브랜드', brand],
    [
      '목적',
      t(
        '인스타그램, 블로그를 통해 {{brand}} 주력제품의 다양한 라이프스타일을 선보이는 콘텐츠 제작',
        { brand, ...NO_ESCAPE },
      ),
    ],
    ['기간', `${report.year ?? ''}-${report.month ?? ''}`],
  ];
  return (
    <div className="form-group">
      <table>
        <tbody>
          <tr>
            <th colSpan={2} className="text-center">
              {t('{{brand}} 마케팅 진행의 건', { brand, ...NO_ESCAPE })}
            </th>
          </tr>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th className="text-center">{t(label)}</th>
              <td className="text-left">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReportDetailTable = ({
  report,
  t,
}: {
  report: InfluencerMonthlyReport;
  t: TFunction;
}) => {
  const cost = report.cost ?? {};
  const exposures = listOf(report.exposures)
    .map((e) =>
      t('{{channel}} {{contents}}건', {
        channel: e.channelName ?? '',
        contents: e.contentsCount ?? '',
        ...NO_ESCAPE,
      }),
    )
    .join(' / ');
  return (
    <div className="form-group">
      <table>
        <tbody>
          <tr>
            <th colSpan={5} className="text-center">
              {t('진행상세')}
            </th>
          </tr>
          <tr>
            <th className="text-center">{t('진행제품')}</th>
            <td colSpan={4} className="text-left">
              {listOf(report.categories).join(' / ')}
            </td>
          </tr>
          <tr>
            <th className="text-center">{t('진행도메인')}</th>
            <td colSpan={4} className="text-left">
              {listOf(report.domains).join(' / ')}
            </td>
          </tr>
          <tr>
            <th className="text-center">{t('노출')}</th>
            <td colSpan={4} className="text-left">
              {exposures}
            </td>
          </tr>
          <tr>
            <th rowSpan={2} className="text-center">
              {t('비용')}
            </th>
            <th className="text-center">{t('제품 수')}</th>
            <th className="text-center">{t('증정 수량')}</th>
            <th className="text-center">{t('TAG가')}</th>
            <th className="text-center">{t('TAG 1/4가')}</th>
          </tr>
          <tr>
            <td className="text-right">
              {formatInfluencerNumber(cost.productCount)}
            </td>
            <td className="text-right">
              {formatInfluencerNumber(cost.distCount)}
            </td>
            <td className="text-right">
              {formatInfluencerNumber(cost.tagPriceSum)}
            </td>
            <td className="text-right">
              {formatInfluencerNumber(cost.tagPricePortion)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const PerformanceTable = ({
  title,
  nameLabel,
  rows,
  showRows,
  t,
}: {
  title: string;
  nameLabel: string;
  rows: InfluencerPerformance[];
  showRows: boolean;
  t: TFunction;
}) => {
  const total = sumInfluencerPerformances(rows);
  return (
    <div className="form-group">
      <label className="label">{t(title)}</label>
      <table>
        <thead>
          <tr>
            <th className="text-center">No</th>
            <th className="text-center">{t(nameLabel)}</th>
            <th className="text-center">{t('컨텐츠 수')}</th>
            <th className="text-center">{t('인게이지먼트')}</th>
            <th className="text-center">{t('제품총액')}</th>
          </tr>
        </thead>
        {showRows && (
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="text-center">{i + 1}</td>
                <td className="text-center">{row.name ?? ''}</td>
                <td className="text-right">
                  {formatInfluencerNumber(row.contentsCount)}
                </td>
                <td className="text-right">
                  {formatInfluencerNumber(row.engagement)}
                </td>
                <td className="text-right">
                  {formatInfluencerNumber(row.tagPriceSum)}
                </td>
              </tr>
            ))}
            <tr>
              <th colSpan={2} className="text-center">
                {t('합계')}
              </th>
              <th className="text-right">
                {formatInfluencerNumber(total.contentsCount)}
              </th>
              <th className="text-right">
                {formatInfluencerNumber(total.engagement)}
              </th>
              <th className="text-right">
                {formatInfluencerNumber(total.tagPriceSum)}
              </th>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
};

const InfluencerMonthlyReportContent = ({ report, t }: ContentProps) => (
  <>
    {report && <ReportInfoTable report={report} t={t} />}
    {report && <ReportDetailTable report={report} t={t} />}
    <PerformanceTable
      title="채널별"
      nameLabel="채널"
      rows={listOf(report?.channelPerformances)}
      showRows={!!report}
      t={t}
    />
    <PerformanceTable
      title="도메인별"
      nameLabel="도메인"
      rows={listOf(report?.domainPerformances)}
      showRows={!!report}
      t={t}
    />
    <PerformanceTable
      title="카테고리별"
      nameLabel="카테고리"
      rows={listOf(report?.categoryPerformances)}
      showRows={!!report}
      t={t}
    />
  </>
);

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌. 표 구성·문구는 레거시 저장 HTML 과 같은 모양 */
export const buildInfluencerMonthlyReportContentHtml = (
  props: ContentProps,
): string =>
  renderToStaticMarkup(<InfluencerMonthlyReportContent {...props} />);
